import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Pencil } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { StandardButton, TertiaryIconButton } from '@/styles/StandardButtons';
import { GenericTag } from '@/styles/GenericTag';
import { SelectionOptionCard, type OptionCardData } from './SelectionOptionCard';
import { colors } from '@/design-system/colors';
import { formatKPIValue } from '@/styles/GenericKPIDashboard';
import { useOptionThumbnails } from './useOptionThumbnails';
import { useElementWidth } from './useElementWidth';
import type { Selection } from './store';

interface SelectionCardViewProps {
  selections: Selection[];
  projectName: (projectId: string) => string;
  /** Approve / un-approve (select / deselect) an option. Persisted by the caller through the API (auto-save). */
  onToggleOption: (selection: Selection, option: OptionCardData) => void | Promise<void>;
  onOpenSelection: (selection: Selection) => void;
  /** Option currently being saved (its control is disabled meanwhile). */
  busyOptionId?: string | null;
  /** Changes when the data was reloaded so thumbnails refresh. */
  dataVersion?: unknown;
  loading?: boolean;
  /** Reorder mode: tiles collapse, show a drag handle, and dragging reports the new order through onOrderChange (nothing is saved here). */
  reorderMode?: boolean;
  onOrderChange?: (orderedIds: string[]) => void;
}

/**
 * Narrowest a tile can be (px) and still show the Description. Below it the
 * Description is the FIRST thing to go -- Title, Status and the options are
 * never hidden for space.
 */
const DESCRIPTION_MIN_TILE_WIDTH = 440;

/**
 * Subtotal of the options currently selected, on one line: "Builder: $0   Client: $4".
 * Builder only appears when the server sent it (the caller holds
 * selections.view_builder_price); a client only ever sees the Client figure.
 */
function SelectedSubtotal({ selection }: { selection: Selection }) {
  const builder = selection.selected_builder_price;
  return (
    <div className="flex flex-wrap items-baseline gap-x-8 text-sm" data-testid={`subtotal-${selection.id}`}>
      {builder !== undefined && (
        <span><span className="text-muted-foreground">Builder: </span><span className="font-semibold">{formatKPIValue(builder, 'currency')}</span></span>
      )}
      <span><span className="text-muted-foreground">Client: </span><span className="font-semibold">{formatKPIValue(selection.selected_client_price, 'currency')}</span></span>
    </div>
  );
}

function SelectionTile({ selection, projectName, isOpen, reorderMode, onToggleOpen, onOpenSelection, thumbnails, busyOptionId, onToggleOption }: {
  selection: Selection;
  projectName: (projectId: string) => string;
  isOpen: boolean;
  reorderMode: boolean;
  onToggleOpen: () => void;
  onOpenSelection: (selection: Selection) => void;
  thumbnails: ReturnType<typeof useOptionThumbnails>;
  busyOptionId?: string | null;
  onToggleOption: SelectionCardViewProps['onToggleOption'];
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: selection.id, disabled: !reorderMode });
  const [widthRef, width] = useElementWidth<HTMLDivElement>();
  const showDescription = !!selection.description && (width === null || width >= DESCRIPTION_MIN_TILE_WIDTH);
  const expanded = isOpen && !reorderMode;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : undefined,
        // A completed Selection is tinted so what is still missing stands out.
        backgroundColor: selection.status === 'completed' ? colors.tileCompletedBg : undefined,
      }}
      className="rounded-lg border-2 border-border bg-card"
      data-testid={`selection-tile-${selection.id}`}
      data-status={selection.status}
    >
      <div ref={widthRef} className="flex items-center gap-2 p-4">
        {reorderMode && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            aria-label={`Drag to reorder ${selection.name}`}
            title="Drag to reorder"
            className="flex h-8 w-6 flex-shrink-0 cursor-grab touch-none items-center justify-center text-gray-400 active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          onClick={reorderMode ? undefined : onToggleOpen}
          aria-expanded={expanded}
        >
          {!reorderMode && (expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />)}
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold" title={selection.name}>{selection.name}</div>
            {showDescription && <div className="truncate text-sm text-muted-foreground" title={selection.description ?? undefined}>{selection.description}</div>}
            <SelectedSubtotal selection={selection} />
          </div>
          <GenericTag type="status" value={selection.status} />
        </button>
        {!reorderMode && (
          <TertiaryIconButton type="button" aria-label="Edit selection" title="Edit selection" className="h-8 w-8 flex-shrink-0" onClick={() => onOpenSelection(selection)}>
            <Pencil className="h-4 w-4" />
          </TertiaryIconButton>
        )}
      </div>

      {expanded && (
        <div className="space-y-3 border-t-2 border-border p-4">
          {selection.options.length === 0 && <p className="text-sm text-muted-foreground">No options yet.</p>}
          {selection.options.map((option) => (
            <SelectionOptionCard
              key={option.id}
              option={{ id: option.id, name: option.name, description: option.description, link: option.link, client_price: option.client_price, is_selected: option.is_selected }}
              thumbnail={thumbnails[option.id]}
              disabled={busyOptionId === option.id}
              onToggle={(opt) => onToggleOption(selection, opt)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Card View: one tile per Selection (two per row on wide screens, one on
 * narrow ones), expandable. Shows ONLY Client Price -- Builder Price never
 * reaches this component. The same tile is what the Client Portal will use:
 * everything it renders comes from the server payload, which is already
 * filtered for the caller.
 */
export function SelectionCardView({ selections, projectName, onToggleOption, onOpenSelection, busyOptionId, dataVersion, loading, reorderMode = false, onOrderChange }: SelectionCardViewProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const allExpanded = selections.length > 0 && selections.every((s) => expanded.has(s.id));

  const expandedOptionIds = useMemo(
    () => (reorderMode ? [] : selections.filter((s) => expanded.has(s.id)).flatMap((s) => s.options.map((o) => o.id))),
    [selections, expanded, reorderMode]
  );
  const thumbnails = useOptionThumbnails(expandedOptionIds, dataVersion);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !onOrderChange) return;
    const ids = selections.map((s) => s.id);
    onOrderChange(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))));
  };

  const toggleOne = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(selections.map((s) => s.id)));

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading selections…</div>;
  if (selections.length === 0) return <div className="py-8 text-center text-muted-foreground">No selections found</div>;

  return (
    <div className="space-y-4">
      {!reorderMode && (
        <div className="flex justify-end">
          <StandardButton type="button" customVariant="white" onClick={toggleAll}>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </StandardButton>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={selections.map((s) => s.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            {selections.map((selection) => (
              <SelectionTile
                key={selection.id}
                selection={selection}
                projectName={projectName}
                isOpen={expanded.has(selection.id)}
                reorderMode={reorderMode}
                onToggleOpen={() => toggleOne(selection.id)}
                onOpenSelection={onOpenSelection}
                thumbnails={thumbnails}
                busyOptionId={busyOptionId}
                onToggleOption={onToggleOption}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
